import React, { useEffect, useRef, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { push } from 'connected-react-router';
import { includes, isEmpty } from 'lodash';
import { fetchDatatableData } from '@actions/data-table-actions';
import * as colors from '@constants/colors';
import {
  getEntitiesListConfig,
  getEntityTypeLabel
} from '@constants/config';
import { dataTableEmptyResultsIconStyles } from '@constants/mui-theme';
import EmptyResultsIcon from '@icons/empty-results-icon';
import { Button, Icon } from '@mui';
import {
  getAllPortalFilters,
  getDataTableColumns,
  getFiltersSelector,
  getLoader,
  optimizeRowsForDataTable
} from '@selectors/data-table-selector';
import Body from '@shared/data-table/body';
import Header from '@shared/data-table/header';
import DotmapsLoader from '@shared/dotmaps-loader';
import { getEmptyLabel } from '@utils/data-table-utils';
import { canAccessEntity, canAccessGroup } from '@utils/permission-utils';
import { shallowEqual } from '@utils/react-utils';
import './data-table.scss';

/**
 * Pure render component for data table (no data fetching).
 * Used by both DataTable and PortalDataTable.
 */
export const DataTableView = memo(({
  columns,
  dataType,
  getIsDataTypeEmpty,
  loader,
  push: pushFn,
  rows,
  subType
}) => {
  const onScroll = useCallback(event => {
    const table = document.getElementById('table-content-container-fixed');
    if (event.currentTarget.scrollLeft > 0) {
      table.style = 'border-right: 2px solid #EBEBEB; box-shadow: 3px 0 5px -2px #EBEBEB;';
    } else {
      table.style = 'border: none: box-shadow: none;';
    }
  }, []);

  const handleCreateDataType = useCallback(() => {
    if (dataType === 'group') {
      pushFn(`/group/${subType}/new`);
      return;
    }
    pushFn(`/${dataType}/new`);
  }, [pushFn, dataType, subType]);

  const getEmptyDataTypeCreateButton = useCallback(() => {
    if (
      (includes(getEntitiesListConfig(), dataType) && canAccessEntity(null, dataType, 'add')) ||
      (dataType === 'group' && canAccessGroup(subType, 'add'))
    ) {
      const label = getEntityTypeLabel(dataType);
      return (
        <Button
          plain
          color="primary"
          onClick={handleCreateDataType}
          variant="contained"
        >
          create {label}
        </Button>
      );
    }
    return null;
  }, [dataType, subType, handleCreateDataType]);

  const columnsFixed = columns ? columns.slice(0, 1) : null;
  const columnsScroll = columns ? columns.slice(1) : null;
  const isFilteredDataTypeEmpty = isEmpty(rows);
  const isDataTypeEmpty = getIsDataTypeEmpty();

  return (
    <div styleName="datatable-container">
      <DotmapsLoader display={loader} />
      {!isFilteredDataTypeEmpty && !isDataTypeEmpty && (
        <div id="table-content" styleName="table-content" onScroll={onScroll}>
          <table
            id="table-content-container-fixed"
            styleName="table-content-container table-content-fixed"
            role="table"
            aria-label={`${dataType || 'Data'} table`}
          >
            {columnsFixed && <Header columns={columnsFixed} dataType={dataType} subType={subType} isFixed />}
            {columnsFixed && <Body columns={columnsFixed} rows={rows} dataType={dataType} isFixed subType={subType} />}
          </table>
          <table
            id="table-content-container-scroll"
            styleName="table-content-container table-content-scroll"
            role="table"
            aria-label={`${dataType || 'Data'} table details`}
          >
            {columnsScroll && <Header columns={columnsScroll} dataType={dataType} subType={subType} />}
            {columnsScroll && <Body columns={columnsScroll} rows={rows} dataType={dataType} subType={subType} />}
          </table>
        </div>
      )}
      {isFilteredDataTypeEmpty && !loader && !isDataTypeEmpty && (
        <div styleName="empty-results" role="status" aria-live="polite">
          <div styleName="icon">
            <EmptyResultsIcon {...dataTableEmptyResultsIconStyles} />
          </div>
          <div styleName="title">No results found</div>
          <div styleName="text">Try searching again or using different filters.</div>
        </div>
      )}
      {isDataTypeEmpty && !loader && (
        <div styleName="empty-results">
          <div styleName="icon">
            <Icon
              color={colors.dotmapsGrayEmptyIcon}
              role="img"
              style={{ fontSize: '56px' }}
            >
              view_list
            </Icon>
          </div>
          <div styleName="title">{getEmptyLabel(dataType)}</div>
          <div styleName="text">{getEmptyDataTypeCreateButton()}</div>
        </div>
      )}
    </div>
  );
});

DataTableView.propTypes = {
  columns: PropTypes.array,
  dataType: PropTypes.string,
  getIsDataTypeEmpty: PropTypes.func.isRequired,
  loader: PropTypes.bool,
  push: PropTypes.func.isRequired,
  rows: PropTypes.array,
  subType: PropTypes.string
};

/**
 * DataTable component with data fetching logic.
 * @returns {React.ReactElement} The DataTable component.
 */
const DataTable = ({ dataType, subType }) => {
  const dispatch = useDispatch();

  const stats = useSelector(state => state.dataTypes.stats?.stats);
  const filters = useSelector(state => getFiltersSelector(state, { dataType, subType }));
  const rawFilters = useSelector(getAllPortalFilters);
  const columns = useSelector(state => getDataTableColumns(state, { dataType, subType }));
  const loader = useSelector(state => getLoader(state, { dataType, subType }));
  const rows = useSelector(state => optimizeRowsForDataTable(dataType, subType)(state));

  const dataTypesCount = stats ? stats.count : null;

  const prevDataTypeRef = useRef(dataType);
  const prevSubTypeRef = useRef(subType);
  const prevRawFiltersRef = useRef(rawFilters);

  const getIsDataTypeEmpty = useCallback(() => {
    if (dataTypesCount) {
      if (dataType === 'overlap') {
        return dataTypesCount.overlap[subType] === 0;
      }
      return dataTypesCount[dataType] === 0;
    }
    return false;
  }, [dataTypesCount, dataType, subType]);

  // Initial fetch on mount
  useEffect(() => {
    dispatch(fetchDatatableData(dataType, subType, filters));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle dataType/subType changes and filter changes
  useEffect(() => {
    const prevDataType = prevDataTypeRef.current;
    const prevSubType = prevSubTypeRef.current;
    const prevRawFilters = prevRawFiltersRef.current;

    if (dataType !== prevDataType || (dataType === prevDataType && subType !== prevSubType)) {
      const isFilteredEmpty = isEmpty(rows);
      const isDataTypeEmpty = getIsDataTypeEmpty();
      if (isFilteredEmpty && !isDataTypeEmpty) {
        dispatch(fetchDatatableData(dataType, subType, filters));
        prevDataTypeRef.current = dataType;
        prevSubTypeRef.current = subType;
        prevRawFiltersRef.current = rawFilters;
        return;
      }
    }

    let prevRawFiltersForType = prevRawFilters?.[dataType];
    let thisRawFiltersForType = rawFilters?.[dataType];
    if (subType && subType !== dataType) {
      prevRawFiltersForType = R.pathOr({}, [subType], prevRawFiltersForType);
      thisRawFiltersForType = R.pathOr({}, [subType], thisRawFiltersForType);
    }
    if (!shallowEqual(prevRawFiltersForType, thisRawFiltersForType)) {
      dispatch(fetchDatatableData(dataType, subType, filters));
    }

    prevDataTypeRef.current = dataType;
    prevSubTypeRef.current = subType;
    prevRawFiltersRef.current = rawFilters;
  }, [dataType, subType, rawFilters, rows, filters, dispatch, getIsDataTypeEmpty]);

  const handlePush = useCallback(url => dispatch(push(url)), [dispatch]);

  return (
    <DataTableView
      columns={columns}
      dataType={dataType}
      getIsDataTypeEmpty={getIsDataTypeEmpty}
      loader={loader}
      push={handlePush}
      rows={rows}
      subType={subType}
    />
  );
};

DataTable.propTypes = {
  dataType: PropTypes.string,
  subType: PropTypes.string
};

export default memo(DataTable);
